import { startCase } from "lodash-es";

import { FeatureFlag, ForEntity } from "@/types";
import {
  OrganizationHierarchyResponse,
  CMWFeatureFlagWithActor,
} from "@/pages/CMWFeatureFlags/types";

import { FLAG_CATALOG, FlagCategory } from "./flag-catalog";

export type FlagStatus = "global" | "partial" | "off";

export const FLAG_STATUS_LABELS: Record<FlagStatus, string> = {
  global: "Global",
  partial: "Partial",
  off: "Off",
};

export interface EntityRef {
  id: number;
  label: string;
}

export interface FlagInsight {
  name: string;
  displayName: string;
  description: string | null;
  category: FlagCategory | null;
  documented: boolean;
  status: FlagStatus;
  globallyEnabled: boolean;
  enabledOrganizations: EntityRef[];
  disabledOrganizations: EntityRef[];
  enabledGroups: EntityRef[];
  disabledGroups: EntityRef[];
  enabledUsers: EntityRef[];
  disabledUsers: EntityRef[];
  totalGates: number;
  /** Globally enabled, but explicitly disabled for at least one org/group/user */
  hasExceptions: boolean;
}

function entityRefsFromGates(
  flag: FeatureFlag,
  entity: ForEntity["entity"],
  enabled: boolean
): EntityRef[] {
  return flag.gates
    .filter(
      (gate) =>
        typeof gate.for === "object" &&
        gate.for.entity === entity &&
        gate.enabled === enabled
    )
    .map((gate) => {
      const target = gate.for as ForEntity;
      return {
        id: target.entity_id,
        label: target.label || `${startCase(entity)} #${target.entity_id}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildFlagInsight(flag: FeatureFlag): FlagInsight {
  const globalGate = flag.gates.find((gate) => gate.for === "global");
  const globallyEnabled = globalGate?.enabled ?? false;

  const enabledOrganizations = entityRefsFromGates(flag, "organization", true);
  const disabledOrganizations = entityRefsFromGates(
    flag,
    "organization",
    false
  );
  const enabledGroups = entityRefsFromGates(flag, "group", true);
  const disabledGroups = entityRefsFromGates(flag, "group", false);
  const enabledUsers = entityRefsFromGates(flag, "user", true);
  const disabledUsers = entityRefsFromGates(flag, "user", false);

  const status: FlagStatus = globallyEnabled
    ? "global"
    : enabledOrganizations.length || enabledGroups.length || enabledUsers.length
      ? "partial"
      : "off";

  const catalogEntry = FLAG_CATALOG[flag.name];

  return {
    name: flag.name,
    displayName: startCase(flag.name),
    description: catalogEntry?.description ?? null,
    category: catalogEntry?.category ?? null,
    documented: !!catalogEntry,
    status,
    globallyEnabled,
    enabledOrganizations,
    disabledOrganizations,
    enabledGroups,
    disabledGroups,
    enabledUsers,
    disabledUsers,
    totalGates: flag.gates.length,
    hasExceptions:
      globallyEnabled &&
      disabledOrganizations.length +
        disabledGroups.length +
        disabledUsers.length >
        0,
  };
}

export function buildFlagInsights(flags: FeatureFlag[]): FlagInsight[] {
  return flags
    .map(buildFlagInsight)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface DashboardMetrics {
  totalFlags: number;
  globallyEnabled: number;
  globallyEnabledWithExceptions: number;
  partiallyEnabled: number;
  off: number;
  withUserOverrides: number;
  undocumented: number;
  totalGates: number;
}

export function buildDashboardMetrics(
  insights: FlagInsight[]
): DashboardMetrics {
  return insights.reduce<DashboardMetrics>(
    (metrics, insight) => {
      metrics.totalFlags += 1;
      metrics.totalGates += insight.totalGates;

      if (insight.status === "global") {
        metrics.globallyEnabled += 1;
        if (insight.hasExceptions) {
          metrics.globallyEnabledWithExceptions += 1;
        }
      } else if (insight.status === "partial") {
        metrics.partiallyEnabled += 1;
      } else {
        metrics.off += 1;
      }

      if (insight.enabledUsers.length || insight.disabledUsers.length) {
        metrics.withUserOverrides += 1;
      }
      if (!insight.documented) {
        metrics.undocumented += 1;
      }

      return metrics;
    },
    {
      totalFlags: 0,
      globallyEnabled: 0,
      globallyEnabledWithExceptions: 0,
      partiallyEnabled: 0,
      off: 0,
      withUserOverrides: 0,
      undocumented: 0,
      totalGates: 0,
    }
  );
}

/**
 * Gate labels only exist on the flag payloads (SupportRent injects them
 * server-side), so build id -> label maps across every flag to resolve names
 * for orgs/groups that only appear in the hierarchy response.
 */
export function buildEntityLabelMaps(flags: FeatureFlag[]): {
  orgLabels: Map<number, string>;
  groupLabels: Map<number, string>;
} {
  const orgLabels = new Map<number, string>();
  const groupLabels = new Map<number, string>();

  for (const flag of flags) {
    for (const gate of flag.gates) {
      if (typeof gate.for !== "object" || !gate.for.label) {
        continue;
      }
      if (gate.for.entity === "organization") {
        orgLabels.set(gate.for.entity_id, gate.for.label);
      } else if (gate.for.entity === "group") {
        groupLabels.set(gate.for.entity_id, gate.for.label);
      }
    }
  }

  return { orgLabels, groupLabels };
}

export interface OrgFlagBreakdown {
  orgId: number;
  orgLabel: string;
  /** true = enabled at org level, false = disabled at org level, null = no org-level gate */
  orgGate: boolean | null;
  enabledGroups: EntityRef[];
  disabledGroups: EntityRef[];
  enabledUserOverrides: number;
  disabledUserOverrides: number;
}

function matchFlag(
  flags: CMWFeatureFlagWithActor[] | undefined,
  flagName: string
): CMWFeatureFlagWithActor[] {
  return (flags || []).filter((flag) => flag.name === flagName);
}

/**
 * Per-organization breakdown of a single flag, built from the
 * organization-hierarchy endpoint (which is what knows group -> org
 * ownership). Only orgs with at least one gate for the flag are returned,
 * sorted by enabled group count, then org-level enablement, then label.
 */
export function buildOrgBreakdown(
  flagName: string,
  hierarchy: OrganizationHierarchyResponse | undefined,
  orgLabels: Map<number, string>,
  groupLabels: Map<number, string>
): OrgFlagBreakdown[] {
  if (!hierarchy) {
    return [];
  }

  const rows: OrgFlagBreakdown[] = [];

  for (const [orgIdStr, orgData] of Object.entries(hierarchy.organizations)) {
    if (orgIdStr === "all") {
      continue;
    }

    const orgGates = matchFlag(orgData.organization_level_flags, flagName);
    const groupGates = matchFlag(orgData.group_level_flags, flagName);
    const userGates = matchFlag(orgData.user_level_flags, flagName);

    if (!orgGates.length && !groupGates.length && !userGates.length) {
      continue;
    }

    const orgId = Number(orgIdStr);
    const toGroupRef = (gate: CMWFeatureFlagWithActor): EntityRef => ({
      id: gate.actor_id,
      label: groupLabels.get(gate.actor_id) || `Group #${gate.actor_id}`,
    });

    rows.push({
      orgId,
      orgLabel: orgLabels.get(orgId) || `Organization #${orgId}`,
      orgGate: orgGates.length ? orgGates[0].enabled : null,
      enabledGroups: groupGates.filter((gate) => gate.enabled).map(toGroupRef),
      disabledGroups: groupGates
        .filter((gate) => !gate.enabled)
        .map(toGroupRef),
      enabledUserOverrides: userGates.filter((gate) => gate.enabled).length,
      disabledUserOverrides: userGates.filter((gate) => !gate.enabled).length,
    });
  }

  return rows.sort(
    (a, b) =>
      b.enabledGroups.length - a.enabledGroups.length ||
      Number(b.orgGate === true) - Number(a.orgGate === true) ||
      a.orgLabel.localeCompare(b.orgLabel)
  );
}
